import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import PublishIcon from '@mui/icons-material/Publish';

import { useMemo } from 'react';

import { InventoryListingStatus } from '@ekhodealer/ekho-common';

import type { IInventoryItem} from '@ekhodealer/ekho-common';
import type { UseQueryResult } from '@tanstack/react-query';
import type { CategorizedPublishItems } from '../../hooks/usePublishSelectedInventory';

import { useGetInventoryItems } from '../../api/useGetInventoryItems';

import { ActionConfirmationModal, type CategoryRow } from './ActionConfirmationModal';

interface PublishConfirmationModalProps {
	isOpen: boolean;
	onOpenChange: (open: boolean) => void;
	selectedIds: Set<string>;
	totalSelected: number;
	onConfirm: (readyItems: IInventoryItem[]) => void;
	onCancel: () => void;
	/** Number of items publish was initiated for (0 means not yet confirmed) */
	publishInitiatedCount: number;
	/** Whether the publish operation is currently pending */
	isPublishing?: boolean;
}

/**
 * Categorize items into ready, alreadyPublished, and notEligible groups
 */
const categorizePublishItems = (
	items: UseQueryResult<IInventoryItem, Error>[],
): CategorizedPublishItems => {
	const result: CategorizedPublishItems = {
		ready: [],
		alreadyPublished: [],
		notEligible: [],
	};

	for (const queryResult of items) {
		if (queryResult.error || !queryResult.data) {
			continue;
		}

		const item = queryResult.data;
		const status = item.listing?.status || InventoryListingStatus.DRAFT;

		if (status === InventoryListingStatus.DRAFT) {
			result.ready.push(item);
		} else if (status === InventoryListingStatus.PUBLISHED) {
			result.alreadyPublished.push(item);
		} else {
			// INCOMPLETE_DRAFT, HIDDEN, or ARCHIVED
			result.notEligible.push(item);
		}
	}

	return result;
};

/**
 * Modal for confirming publish operation on selected inventory items
 * Fetches items using query hooks and categorizes them
 * Shows categorized items: ready to publish, already published, and not eligible
 * After confirmation, shows immediate success message
 */
export const PublishConfirmationModal = ({
	isOpen,
	onOpenChange,
	selectedIds,
	totalSelected,
	onConfirm,
	onCancel,
	publishInitiatedCount,
	isPublishing = false,
}: PublishConfirmationModalProps) => {
	// Fetch all selected items using query hooks
	const itemQueries = useGetInventoryItems(selectedIds, isOpen && selectedIds.size > 0);

	// Categorize items after fetching
	const categorizedItems = useMemo(() => {
		if (!isOpen || selectedIds.size === 0) {
			return { ready: [], alreadyPublished: [], notEligible: [] };
		}
		return categorizePublishItems(itemQueries);
	}, [isOpen, selectedIds.size, itemQueries]);

	const { ready, alreadyPublished, notEligible } = categorizedItems;

	// Check if we're still loading
	const isLoading = itemQueries.some((query) => query.isPending);

	// Build category rows for the generic modal
	const categories: CategoryRow[] = [
		{
			icon: <CheckCircleIcon />,
			iconColor: 'var(--color-positive, #22c55e)',
			count: ready.length,
			message: ready.length === 1 ? 'is ready to publish' : 'are ready to publish',
		},
		{
			icon: <InfoIcon />,
			iconColor: 'var(--color-secondary, #6b7280)',
			count: alreadyPublished.length,
			message:
				alreadyPublished.length === 1 ? 'is already published' : 'are already published',
		},
		{
			icon: <ErrorIcon />,
			iconColor: 'var(--color-negative, #ef4444)',
			count: notEligible.length,
			message: notEligible.length === 1 ? 'cannot be published' : 'cannot be published',
		},
	];

	const handleConfirm = () => {
		onConfirm(ready);
	};

	return (
		<ActionConfirmationModal
			isOpen={isOpen}
			onOpenChange={onOpenChange}
			categories={categories}
			totalSelected={totalSelected}
			readyCount={ready.length}
			onConfirm={handleConfirm}
			onCancel={onCancel}
			actionInitiatedCount={publishInitiatedCount}
			isConfirming={isPublishing || isLoading}
			title='Review before publishing'
			subtitle='You selected {count} inventory item{plural}'
			successTitle='Items published'
			successIcon={<PublishIcon />}
			successMessage='item{plural} published'
			successDescription='The selected items have been published and are now live on your storefront.'
			confirmButtonText='Publish {count} now'
			noItemsMessage='No items can be published. Only draft items can be published.'
		/>
	);
};
